'use client'

import { createContext, PropsWithChildren, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useOurStore } from '@/app/_providers/StoreProvider'
import { CartResetType, useReset } from '@/hooks/useReset'
import { QUERY_KEYS } from '@/config/tanstack-querykeys.config'
import { getCartAction } from '@/app/_actions/cart.actions'
import { unwrapActionResult } from '@/utils/errors.utils'
import { cartExists } from '@/utils/cart.utils'
import { uninitializedCart } from '@/config/cart.config'
import TestState from '@/app/_components/TestState/TestState'

export type CartContextT = {
  cart: Transformed.Cart
  loading: boolean
  error: Error | null
  initialFetchCompleted: boolean
}

export const CartContext = createContext<CartContextT>({
  cart: uninitializedCart,
  error: null,
  loading: false,
  initialFetchCompleted: false,
})

const CartProvider = ({ children }: PropsWithChildren): ReactNode => {
  const [initialFetchCompleted, setInitialFetchCompleted] = useState(false)
  const cartId = useOurStore((state) => state.cartId)
  // eslint-disable-next-line no-underscore-dangle
  const hasHydrated = useOurStore((state) => state._hasHydrated)
  const localLineItems = useOurStore((state) => state.localLineItems)
  const lineItemsToDelete = useOurStore((state) => state.lineItemsToDelete)
  const [augmentedCart, setAugmentedCart] = useState(uninitializedCart)
  const { doReset } = useReset()
  const handlingErrorRef = useRef(false)

  const cartQuery = useQuery({
    queryKey: [QUERY_KEYS.CART, cartId],
    queryFn: () => getCartAction(cartId as string).then(unwrapActionResult),
    enabled: !!cartId && cartExists(cartId),
    retry: 2,
  })

  useEffect(() => {
    if (hasHydrated && !cartId) {
      setInitialFetchCompleted(true)
    }
  }, [cartId, hasHydrated])

  const onErrorCallback = (): void => {
    if (cartId && cartExists(cartId)) {
      // eslint-disable-next-line no-console
      console.error('Error fetching cart:', cartQuery.error)
      setAugmentedCart(uninitializedCart)
      doReset({ type: CartResetType.Full })
    }
  }

  useEffect(() => {
    if (cartQuery.data) {
      setInitialFetchCompleted(true)
      // TODO: handle cart augmentation
      setAugmentedCart(cartQuery.data)
    }
  }, [cartQuery.data, lineItemsToDelete, localLineItems])

  useEffect(() => {
    if (hasHydrated && (!cartId || !cartExists(cartId))) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAugmentedCart(uninitializedCart)
    }
  }, [cartId, hasHydrated])

  useEffect(() => {
    if (handlingErrorRef.current) return
    if (cartQuery.error && !cartQuery.isLoading) {
      handlingErrorRef.current = true
      onErrorCallback()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartQuery.isLoading, cartQuery.error])

  const value = useMemo(() => ({
    cart: augmentedCart,
    loading: cartQuery.isLoading,
    error: cartQuery.error,
    initialFetchCompleted,
  }), [augmentedCart, cartQuery.isLoading, cartQuery.error, initialFetchCompleted])

  return (
    <CartContext.Provider value={value}>
      <TestState id="test-state-cart" data={augmentedCart} />
      {children}
    </CartContext.Provider>
  )
}

export const useCart = (): CartContextT => {
  const cartContext = useContext(CartContext)

  if (!cartContext) {
    throw Error('Cart context is undefined. Ensure CartProvider is wrapping the component tree.')
  }

  return cartContext
}

export default CartProvider
